'use client';

export interface AuthenticatedUser {
  id: string;
  name: string;
  mail: string;
  hasEarit?: boolean;
  earitLicence?: string;
  location?: unknown;
  closeContacts?: unknown[];
}

const AUTH_ME_CACHE_TTL_MS = 5000;

let cachedUser: AuthenticatedUser | null | undefined;
let cacheExpiresAt = 0;
let inFlightRequest: Promise<AuthenticatedUser | null> | null = null;

/**
 * Fetches the authenticated user once and deduplicates concurrent requests.
 */
export async function getCurrentUser(forceRefresh = false): Promise<AuthenticatedUser | null> {
  const now = Date.now();

  if (!forceRefresh && cachedUser !== undefined && now < cacheExpiresAt) {
    return cachedUser;
  }

  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = fetch('/api/auth/me', { credentials: 'include' })
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return (data?.user as AuthenticatedUser | undefined) || null;
    })
    .catch(() => null)
    .then((user) => {
      cachedUser = user;
      cacheExpiresAt = Date.now() + AUTH_ME_CACHE_TTL_MS;
      return user;
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
}

/**
 * Clears the local auth cache (e.g. after logout).
 */
export function clearCurrentUserCache(): void {
  cachedUser = null;
  cacheExpiresAt = 0;
  inFlightRequest = null;
}
